import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { CreateRecordProperties, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { getFormattedTime } from 'webapp/utils/number';
import type { VerificationInfo } from 'webapp/utils/verification';

import type { HorizonMetricRecord, TagRecord, UserRecord } from 'webapp/models';
import type { Metric, Source } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import MetricSetup from 'webapp/models-custom/metric-setup';

export default class HorizonMetric extends BufferedModel<
	HorizonMetricRecord,
	RecordData.HorizonMetric.Serialized
> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	static override modelName = ModelName.HORIZON_METRIC;

	get id(): string | null {
		return this.model?.id || null;
	}

	override get numericId(): number | null {
		return this.id ? +this.id : null;
	}

	override get modelName(): string {
		return ModelName.HORIZON_METRIC;
	}

	get metricName(): string {
		return this.getBuffered('name');
	}

	set metricName(value: string) {
		this.setBuffered('name', value);
	}

	override async save(force = false): Promise<HorizonMetricRecord> {
		const nameWasDirty = this.isPropertyDirty('name');
		const result = await super.save(force);

		// After renaming a horizon metric, sync the new name to the associated
		// Metric record so that the info popup (and anything else reading from
		// metric_keys) shows the updated name immediately.
		if (nameWasDirty) {
			const metric = this.metric;
			if (metric) {
				metric.directPropUpdate({ name: this.metricName });
			}
		}

		return result;
	}

	get spaceAccessId(): number {
		return this.model.spaceAccessId;
	}

	get createdBy(): number {
		return this.model.createdBy;
	}

	get createdById(): number {
		return this.model.createdBy;
	}

	get created(): number {
		return this.model.created;
	}

	get tags(): number[] {
		return this.getBuffered('tags') || [];
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get tagsCount(): number {
		return this.tags.length;
	}

	@cached
	get tagsObjs(): TagRecord[] {
		const tagIds = this.tags;
		return this.mmTagsService.savedRecords.filter((tag) => {
			return tag.id !== null && tag.id !== undefined && tagIds.includes(+tag.id);
		});
	}

	get verificationInfo(): VerificationInfo | undefined {
		return (this.metric?.verificationInfo ?? this.model.verificationInfo) as
			VerificationInfo | undefined;
	}

	get isMine(): boolean {
		return this.createdById === this.currentUserService.numericId;
	}

	@cached
	get createdByUser(): UserRecord | null {
		if (this.createdById) {
			return this.mmUsersService.findRecord(String(this.createdById));
		}
		return null;
	}

	get createdByText(): string {
		return this.createdByUser?.name || '';
	}

	get createdTimeText(): string {
		return getFormattedTime(this.created);
	}

	get canEdit(): boolean {
		return (
			this.currentUserService.isAdmin ||
			String(this.createdById) === this.currentUserService.id
		);
	}

	@cached
	get sourceObj(): Source | null {
		return this.mmSourcesService.findModelQuick(String(this.spaceAccessId));
	}

	get usedSources(): Source[] {
		const source = this.sourceObj;
		return source ? [source] : [];
	}

	get usedSourceNames(): string {
		return this.usedSources.map((s) => s.title).join(' ');
	}

	get usedSourceTypeKeys(): string {
		return this.usedSources.map((s) => s.sourceType.key).join(' ');
	}

	get usedMetricsText(): string {
		return '';
	}

	@cached
	get metric(): Metric | null {
		if (this.numericId) {
			return this.mmMetricsService.getMetricsForHorizonMetricId(this.numericId)[0] || null;
		}
		return null;
	}

	@cached
	get metricSetup(): MetricSetup {
		return new MetricSetup(this.metric?.metricData || undefined);
	}

	get isFavorite(): boolean {
		return !!this.metric?.isFavorite;
	}

	clone(): CreateRecordProperties<HorizonMetricRecord> {
		const data = this.toJSON();
		data.name = `Copy of ${data.name}`;
		return data;
	}
}
